import { apiFetch } from "../../api/apiFetch";

// POST /orders/:id/resolve — shared by the full resolve page and the
// flagged list's quick actions (Refund / Manually ship).

/** Why a resolution didn't land, so the UI can say which. */
export type ResolveOutcome = "ok" | "forbidden" | "conflict" | "failed";

export type ResolveBody = {
    resolved_by_uid: string;
    resolved_by_name: string;
    refund_lines: Array<{ line_position: number | undefined; refund_count: number }>;
    // Quick "manually ship": the marked-short units actually went out in
    // the parcel named by ship_tracking_number. The BE moves the line
    // counters (scanned up, missing/damaged/wrong down) and increments
    // that parcel's recorded contents — a record correction, no booking
    // and no channel push.
    ship_lines?: Array<{ line_position: number | undefined; count: number }>;
    ship_tracking_number?: string;
};

// Signed-in user for the resolved_by_* attribution fields.
export const resolveAttribution = (): { resolved_by_uid: string; resolved_by_name: string } => {
    try {
        const userData = window.localStorage.getItem("user");
        if (userData) {
            const u = JSON.parse(userData);
            return { resolved_by_uid: u?.uid ?? "", resolved_by_name: u?.name ?? "" };
        }
    } catch { /* fall through */ }
    return { resolved_by_uid: "", resolved_by_name: "" };
};

/**
 * POST the resolution and report whether it actually landed.
 *
 * The caller MUST NOT show the success overlay / "refund issued" toast on
 * anything but "ok": this endpoint writes the refund lines and clears the
 * flag, so claiming success on a rejection tells a leader money was refunded
 * when nothing was persisted and the order is still flagged.
 *
 * "forbidden" (403): resolve is admin-only, while the Flagged screen can be
 * opened by anyone with the dispatch.flagged module toggle.
 * "conflict" (409): the order is no longer flagged (resolved elsewhere, a
 * double-click, or a stale list card) — refresh, don't retry.
 */
export const postResolution = async (
    orderId: string,
    body: ResolveBody,
): Promise<ResolveOutcome> => {
    try {
        const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/resolve`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (res.ok) return "ok";
        if (res.status === 403) return "forbidden";
        if (res.status === 409) return "conflict";
        return "failed";
    } catch {
        // Network/parse failure — indistinguishable from "didn't land".
        return "failed";
    }
};
